import { AccountModel } from '~/app/views/Accounts';
import { AccountTypeNumber } from '~/types';
import useCachedQuery from '../useCachedQuery';
import { QueryOptions, QueryResult } from '../useQuery';

/**
 * By default, accounts with one of these types should not be shown
 */
const ACCOUNT_TYPES_HIDDEN_BY_DEFAULT = [
    AccountTypeNumber.TotalFrom,
    AccountTypeNumber.TotalIntervals,
    AccountTypeNumber.SumAlpha,
];

const HEADING_ACCOUNT_TYPES = [AccountTypeNumber.Heading, AccountTypeNumber.HeadingBreak];

/**
 * Holds the logic to show or not show an account given a set of parameters
 */
const checkIfAccountShouldAppear = ({
    account,
    showAllAccountTypes,
    showAllAccounts,
    showHeadingAccounts,
    isBlockedForDirectEntries,
}: {
    account: AccountModel;
    showAllAccountTypes?: boolean;
    showAllAccounts?: boolean;
    showHeadingAccounts?: boolean;
    isBlockedForDirectEntries?: boolean;
}) => {
    // If all accounts should be shown, no filter is done whatsoever
    if (showAllAccounts) {
        return true;
    }

    // If an account is barred (not accessible) or if
    // it doesn't have a defined type, it gets discarded
    if (!account.access || !account.type) {
        return false;
    }

    const isBlockedForDirectEntriesMatch =
        isBlockedForDirectEntries !== undefined ? account.isBlockedForDirectEntries === isBlockedForDirectEntries : true;

    let accountTypesToHide: AccountTypeNumber[] = [];

    if (!showHeadingAccounts) {
        accountTypesToHide = accountTypesToHide.concat(HEADING_ACCOUNT_TYPES);
    }

    if (!showAllAccountTypes) {
        accountTypesToHide = accountTypesToHide.concat(ACCOUNT_TYPES_HIDDEN_BY_DEFAULT);
    }

    // Based on the type of a given account, it checks if it should be shown or not
    const shouldShowAccountsOfThisType = !accountTypesToHide?.includes(account.type.number);

    return isBlockedForDirectEntriesMatch && shouldShowAccountsOfThisType;
};

/**
 * Custom hook responsible to fetch accounts
 */
export function useAccounts({
    showAllAccounts,
    showAllAccountTypes,
    showHeadingAccounts,
    isBlockedForDirectEntries,
    options,
}: {
    showAllAccounts?: boolean;
    showAllAccountTypes?: boolean;
    showHeadingAccounts?: boolean;
    isBlockedForDirectEntries?: boolean;
    options?: QueryOptions;
}): QueryResult<AccountModel[]> {
    const accountsQuery = useCachedQuery<AccountModel[]>('/accounts', [], options);

    const accounts = accountsQuery?.data.filter(account =>
        checkIfAccountShouldAppear({
            account,
            showAllAccounts,
            showAllAccountTypes,
            showHeadingAccounts,
            isBlockedForDirectEntries,
        })
    );

    return {
        ...accountsQuery,
        data: accounts,
    };
}
